/**
 * PawTenant pricing — single source of truth for public/marketing copy AND the
 * client mirror of the server-authoritative charge amounts.
 *
 * These values MUST match Stripe product catalog and checkout logic in
 * src/pages/assessment/page.tsx → getAssessmentBasePrice() and
 * supabase/functions/create-payment-intent (server-side canonical amounts).
 *
 * FINAL structure (owner-approved 2026-07, phased subscriptions):
 *   ESA one-time:          1–2 pets $129 · exactly 3 pets $149 fixed total
 *   PSD one-time:          1–2 dogs $129 · exactly 3 dogs $149 fixed total
 *   ESA/PSD subscription:  1 → $115 first year, then $100/yr
 *                          2 → $115/yr flat (first year AND renewal)
 *                          exactly 3 → $135 first year, then $115/yr
 *                          (SUBSCRIPTION-TWO-ANIMAL-115-FLAT-LIVE-001, 2026-10-01)
 *   ESA/PSD + RA Combo:    one-time $179 · annual $159 FLAT (no year-two drop)
 *   Consultation:          RETIRED (no active $79 purchase path; historical only)
 * No per-pet add-ons anywhere — multi-pet is always a fixed total.
 *
 * The subscription amount charged TODAY is the FIRST-YEAR price; the RENEWAL
 * price is what Stripe bills from year two on (a Subscription Schedule flips the
 * subscription to the renewal Price after the first annual period). Never render
 * the first-year amount as the permanent annual amount.
 */

/* ───────────────────────────────────────────────────────────────────────────
 * CANONICAL MATRIX — the ONE immutable source. Every helper below reads from it.
 * Package identity is NEVER inferred from an amount; it is always looked up by
 * (product, package, plan, tier, phase). Frozen so no caller can mutate it.
 * ─────────────────────────────────────────────────────────────────────────── */

export type Product = "esa" | "psd";
export type Tier = "single" | "multi";
export type PricePhase = "initial" | "renewal";

/** Standard letter matrix, whole dollars. Same numbers for ESA and PSD. */
const STANDARD_MATRIX = Object.freeze({
  oneTime:   Object.freeze({ single: 129, multi: 149 }),
  firstYear: Object.freeze({ single: 115, multi: 135 }),
  renewal:   Object.freeze({ single: 100, multi: 115 }),
});

/** Combo (ESA/PSD + Reasonable Accommodation letter) — flat, no year-two drop. */
const COMBO_MATRIX = Object.freeze({
  oneTime: 179,
  firstYear: 159,
  renewal: 159, // combo does NOT reduce at renewal
});

/** Clamp a raw pet/dog count to the single/multi tier (1 → single, 2–3 → multi). */
export function petTier(petCount: number): Tier {
  const n = Math.max(1, Math.min(3, Math.floor(petCount || 1)));
  return n === 1 ? "single" : "multi";
}

/**
 * ESA-TWO-PET-129-PRICING-001 — ESA Standard ONE-TIME tier rule.
 *
 * The ESA Standard one-time package covers UP TO TWO pets at the single rate
 * ($129); the multi rate ($149) is reserved for EXACTLY three pets.
 *
 * This deliberately differs from petTier(). Scope this rule to the ESA Standard
 * one-time package ONLY — PSD one-time has psdOneTimeTier() and every
 * subscription uses subscriptionTier().
 *
 * Valid counts are the integers 1, 2 and 3. Anything else (0, 4+, decimals,
 * negatives, NaN, non-numeric) is INVALID and yields null. Callers must reject
 * rather than clamp, so a manipulated pet count can never buy a cheaper tier.
 */
export function esaOneTimeTier(petCount: unknown): Tier | null {
  if (typeof petCount !== "number" || !Number.isInteger(petCount)) return null;
  if (petCount < 1 || petCount > 3) return null;
  return petCount <= 2 ? "single" : "multi";
}

/** PSD Standard one-time: 1–2 dogs $129; exactly 3 dogs $149. */
export function psdOneTimeTier(dogCount: unknown): Tier | null {
  if (typeof dogCount !== "number" || !Number.isInteger(dogCount)) return null;
  if (dogCount < 1 || dogCount > 3) return null;
  return dogCount <= 2 ? "single" : "multi";
}

/** Display-only fallback for transient empty form state. Charge paths reject invalid counts. */
function oneTimeTierClamped(count: number): Tier {
  const n = Math.max(1, Math.min(3, Math.floor(Number(count) || 1)));
  return n <= 2 ? "single" : "multi";
}

/**
 * SUBSCRIPTION pricing — BOTH ESA and PSD (SUBSCRIPTION-TWO-ANIMAL-115-FLAT-LIVE-001).
 * Mirrors subscriptionPriceIds() in supabase/functions/_shared/pricingMatrix.ts:
 *     1 animal     →  $115 first year, $100 renewal (single pair)
 *     2 animals    →  $115 every year (the flat $115/yr Price — TWO_ANIMAL_ANNUAL)
 *     exactly 3    →  $135 first year, $115 renewal (multi pair)
 * subscriptionTier() validates counts; invalid counts yield null. Display
 * getters fall back to the same two-animal clamp as one-time (a transient empty
 * form must not crash a page); the server rejects invalid counts before any charge.
 */
export const TWO_ANIMAL_ANNUAL = STANDARD_MATRIX.renewal.multi; // 115 — first year AND renewal
export function subscriptionTier(petCount: unknown): Tier | null {
  if (typeof petCount !== "number" || !Number.isInteger(petCount)) return null;
  if (petCount < 1 || petCount > 3) return null;
  return petCount <= 2 ? "single" : "multi";
}
function subscriptionTierClamped(count: number): Tier {
  return subscriptionTier(count) ?? oneTimeTierClamped(count);
}

/* ───────────────────────────────────────────────────────────────────────────
 * Legacy constant exports — VALUES updated to the new matrix, NAMES preserved so
 * every existing import keeps working. `subscription`/`annual` now carry the
 * FIRST-YEAR amount (what the customer pays today); renewal amounts are separate.
 * ─────────────────────────────────────────────────────────────────────────── */

export const ESA_PRICING = {
  oneTime: STANDARD_MATRIX.oneTime.single,                 // 129
  oneTimeMultiPetTotal: STANDARD_MATRIX.oneTime.multi,     // 149
  subscription: STANDARD_MATRIX.firstYear.single,          // 115 (first year)
  subscriptionMultiPetTotal: STANDARD_MATRIX.firstYear.multi, // 135 (first year)
  renewal: STANDARD_MATRIX.renewal.single,                 // 100
  renewalMultiPetTotal: STANDARD_MATRIX.renewal.multi,     // 115
} as const;

export const PSD_PRICING = {
  oneTime: STANDARD_MATRIX.oneTime.single,                 // 129
  oneTimeMultiDogTotal: STANDARD_MATRIX.oneTime.multi,     // 149
  annual: STANDARD_MATRIX.firstYear.single,               // 115 (first year)
  annualMultiDogTotal: STANDARD_MATRIX.firstYear.multi,   // 135 (first year)
  renewal: STANDARD_MATRIX.renewal.single,                // 100
  renewalMultiDogTotal: STANDARD_MATRIX.renewal.multi,    // 115
  /** RETIRED 2026-07 — no active $79 consultation purchase path. Kept only so
   *  historical references resolve; never surfaced in any new-purchase flow. */
  consultation: 79,
} as const;

export const RENEWAL_PRICING = {
  /** Single-tier annual renewal (back-compat alias). */
  annual: STANDARD_MATRIX.renewal.single,   // 100
  single: STANDARD_MATRIX.renewal.single,   // 100
  multi: STANDARD_MATRIX.renewal.multi,     // 115
} as const;

/**
 * Additional Documentation add-on (optional, purchased AFTER the original letter
 * for a separate landlord / property / HOA form on a STANDARD order). DISPLAY
 * price for the pre-purchase CTA only; the authoritative charge is computed
 * server-side in create-additional-doc-invoice. Out of scope for the phased
 * subscription change.
 */
export const ADDITIONAL_DOC_PRICING = {
  addon: 50,
} as const;

export const BUNDLE_PRICING = {
  /** Flat one-time total for the RA bundle (1–3 pets/dogs). */
  oneTime: COMBO_MATRIX.oneTime,   // 179
  /** Flat annual total for the RA bundle (1–3 pets/dogs) — first year AND renewal. */
  annual: COMBO_MATRIX.firstYear,  // 159
} as const;

export const ESA_PRICE_LABELS = {
  oneTime: `$${STANDARD_MATRIX.oneTime.single}`,             // $129
  oneTimeMultiPetTotal: `$${STANDARD_MATRIX.oneTime.multi}`, // $149
  subscription: `$${STANDARD_MATRIX.firstYear.single}`,      // $115 (first year)
  subscriptionMultiPetTotal: `$${STANDARD_MATRIX.firstYear.multi}`, // $135
  renewal: `$${STANDARD_MATRIX.renewal.single}`,             // $100
  oneTimeSuffix: "one-time",
  subscriptionSuffix: "/year",
  startingFrom: `$${STANDARD_MATRIX.firstYear.single}`,      // $115
} as const;

/* ───────────────────────────────────────────────────────────────────────────
 * Package identity + display names.
 * ─────────────────────────────────────────────────────────────────────────── */

/** Canonical package identifiers used across UI, checkout body, Stripe metadata,
 *  and the orders.package_key column. */
export type PackageKey =
  | "esa_standard"
  | "esa_ra_bundle"
  | "psd_standard"
  | "psd_ra_bundle";

/** Billing plan identifiers stored in orders.billing_plan (distinct from the
 *  legacy display string orders.plan_type). */
export type BillingPlan = "one_time" | "annual";

export const PACKAGE_DISPLAY_NAMES: Record<PackageKey, string> = {
  esa_standard: "ESA Letter",
  esa_ra_bundle: "ESA + Reasonable Accommodation Letter",
  psd_standard: "PSD Documentation",
  psd_ra_bundle: "PSD + Reasonable Accommodation Letter",
};

/** True when a package includes the Reasonable Accommodation letter add-on. */
export function isRaBundle(packageKey: string | null | undefined): boolean {
  return packageKey === "esa_ra_bundle" || packageKey === "psd_ra_bundle";
}

/** Product ("esa" | "psd") for a package key. */
export function packageProduct(packageKey: PackageKey): Product {
  return packageKey === "psd_standard" || packageKey === "psd_ra_bundle" ? "psd" : "esa";
}

/* ───────────────────────────────────────────────────────────────────────────
 * One-time totals (unchanged $129 / $149).
 * ─────────────────────────────────────────────────────────────────────────── */

export function getEsaOneTimeTotal(petCount: number): number {
  // ESA-TWO-PET-129-PRICING-001: 1–2 pets → $129, exactly 3 → $149. Strict —
  // an out-of-range count is a bug, never a cheaper tier.
  const tier = esaOneTimeTier(petCount);
  if (tier === null) {
    throw new RangeError(
      `getEsaOneTimeTotal: ESA Standard one-time covers 1–3 pets; got ${String(petCount)}`,
    );
  }
  return STANDARD_MATRIX.oneTime[tier];
}
export function getPsdOneTimeTotal(dogCount: number): number {
  const tier = psdOneTimeTier(dogCount) ?? oneTimeTierClamped(dogCount);
  return STANDARD_MATRIX.oneTime[tier];
}

/* ───────────────────────────────────────────────────────────────────────────
 * Subscription FIRST-YEAR totals (what the customer pays today: $115 / $135).
 * Legacy names getEsaAnnualTotal / getPsdAnnualTotal are preserved but now
 * return the FIRST-YEAR amount.
 * ─────────────────────────────────────────────────────────────────────────── */

export function getEsaAnnualTotal(petCount: number): number {
  return STANDARD_MATRIX.firstYear[subscriptionTierClamped(petCount)]; // 1–2 → $115, 3 → $135
}
export function getPsdAnnualTotal(dogCount: number): number {
  return STANDARD_MATRIX.firstYear[subscriptionTierClamped(dogCount)]; // 1–2 → $115, 3 → $135
}

/* ───────────────────────────────────────────────────────────────────────────
 * Subscription RENEWAL totals (year two onward: $100 / $115).
 * ─────────────────────────────────────────────────────────────────────────── */

export function getEsaRenewalTotal(petCount: number): number {
  // 1 → $100, 2 → $115 (flat), 3 → $115
  return petCount === 2 ? TWO_ANIMAL_ANNUAL : STANDARD_MATRIX.renewal[subscriptionTierClamped(petCount)];
}
export function getPsdRenewalTotal(dogCount: number): number {
  // 1 → $100, 2 → $115 (flat), 3 → $115
  return dogCount === 2 ? TWO_ANIMAL_ANNUAL : STANDARD_MATRIX.renewal[subscriptionTierClamped(dogCount)];
}

/* ───────────────────────────────────────────────────────────────────────────
 * Combo (RA bundle) totals — flat, no renewal drop.
 * ─────────────────────────────────────────────────────────────────────────── */

export function getBundleOneTimeTotal(): number { return COMBO_MATRIX.oneTime; }   // 179
export function getBundleAnnualTotal(): number { return COMBO_MATRIX.firstYear; }  // 159
export function getBundleRenewalTotal(): number { return COMBO_MATRIX.renewal; }   // 159

/* ───────────────────────────────────────────────────────────────────────────
 * Package resolvers — the amount DUE TODAY (one-time, or subscription first year)
 * and the RENEWAL amount, keyed purely by identity. Mirrors the server exactly.
 * ─────────────────────────────────────────────────────────────────────────── */

/** Amount due today for a package + plan + count (one-time total, or first-year
 *  subscription total). Signature preserved for existing callers. */
export function getPackageTotal(
  packageKey: PackageKey,
  plan: BillingPlan,
  petCount: number,
): number {
  switch (packageKey) {
    case "esa_ra_bundle":
    case "psd_ra_bundle":
      return plan === "annual" ? getBundleAnnualTotal() : getBundleOneTimeTotal();
    case "psd_standard":
      return plan === "annual" ? getPsdAnnualTotal(petCount) : getPsdOneTimeTotal(petCount);
    case "esa_standard":
    default:
      return plan === "annual" ? getEsaAnnualTotal(petCount) : getEsaOneTimeTotal(petCount);
  }
}

/** Renewal amount (year two onward) for a subscription package + count.
 *  Combo stays flat at $159. */
export function getPackageRenewal(packageKey: PackageKey, petCount: number): number {
  switch (packageKey) {
    case "esa_ra_bundle":
    case "psd_ra_bundle":
      return getBundleRenewalTotal();
    case "psd_standard":
      return getPsdRenewalTotal(petCount);
    case "esa_standard":
    default:
      return getEsaRenewalTotal(petCount);
  }
}

/* ───────────────────────────────────────────────────────────────────────────
 * Full price quote — the deterministic contract the checkout disclosure and the
 * server parity test both read. Coupons apply to ONE-TIME purchases only.
 * ─────────────────────────────────────────────────────────────────────────── */

export interface PriceQuote {
  packageKey: PackageKey;
  plan: BillingPlan;
  tier: Tier;
  isCombo: boolean;
  oneTimePrice: number;
  firstYearPrice: number;
  renewalPrice: number;
  /** What Stripe charges today: one-time total, or first-year subscription total. */
  amountDueToday: number;
  discountAmount: number;
  /** amountDueToday − discountAmount (subscriptions never take a public coupon). */
  finalAmount: number;
}

export function quotePackage(
  packageKey: PackageKey,
  plan: BillingPlan,
  petCount: number,
  couponDiscount = 0,
): PriceQuote {
  const isCombo = isRaBundle(packageKey);
  const oneTimePrice = getPackageTotal(packageKey, "one_time", petCount);
  const firstYearPrice = getPackageTotal(packageKey, "annual", petCount);
  const renewalPrice = getPackageRenewal(packageKey, petCount);
  const amountDueToday = plan === "annual" ? firstYearPrice : oneTimePrice;
  // Public coupons apply to one-time purchases only.
  const discountAmount = plan === "one_time" ? Math.max(0, Math.min(couponDiscount, amountDueToday)) : 0;
  // Report the tier that actually priced amountDueToday.
  let pricedTier: Tier;
  if (plan === "one_time" && packageKey === "esa_standard" && !isCombo) {
    pricedTier = esaOneTimeTier(petCount) ?? petTier(petCount);
  } else if (plan === "one_time" && packageKey === "psd_standard" && !isCombo) {
    pricedTier = psdOneTimeTier(petCount) ?? oneTimeTierClamped(petCount);
  } else if (plan === "annual" && !isCombo) {
    // Both ESA and PSD subscriptions tier at 2.
    pricedTier = subscriptionTierClamped(petCount);
  } else {
    pricedTier = petTier(petCount);
  }
  return {
    packageKey,
    plan,
    tier: pricedTier,
    isCombo,
    oneTimePrice,
    firstYearPrice,
    renewalPrice,
    amountDueToday,
    discountAmount,
    finalAmount: Math.max(0, amountDueToday - discountAmount),
  };
}

/* ───────────────────────────────────────────────────────────────────────────
 * Stripe subscription lookup_keys — the canonical keys the server resolves to
 * provisioned Price IDs (pricing_stripe_map). Combo has no phased Price (flat
 * inline price_data), so returns null. Used by the frontend↔server parity test.
 * ─────────────────────────────────────────────────────────────────────────── */

export function subscriptionLookupKeys(
  packageKey: PackageKey,
  petCount: number,
): { firstYear: string; renewal: string } | null {
  if (isRaBundle(packageKey)) return null; // combo = inline flat price, no schedule
  const product = packageProduct(packageKey);
  // Two animals bill the flat $115/yr Price (the 2–3 animal renewal Price) for
  // both the first year and every renewal — no schedule.
  if (petCount === 2) {
    const flat = `pawtenant_${product}_multi_renewal_v1`;
    return { firstYear: flat, renewal: flat };
  }
  const tier = subscriptionTierClamped(petCount);
  return {
    firstYear: `pawtenant_${product}_${tier}_first_year_v1`,
    renewal: `pawtenant_${product}_${tier}_renewal_v1`,
  };
}
